import { SelectedProductData } from "common/components/ProductChooser";
import { useFeatureOn, FeatureName } from "common/Split";
import { clamp, find, includes, isEmpty, isInteger, isNil, map, some, values } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import {
  transferCreateAddProduct,
  transferCreateRemoveProduct,
  transferCreateSetIsEachPickingOrderFlag,
  transferCreateUpdateProductLotNumber,
  transferCreateUpdateProductNumberOfCases,
  transferCreateUpdateProductQty,
} from "transfers/create/actions";
import { CreateTransferLoader } from "transfers/create/CreateTransferLoader.types";
import { addKittedProduct } from "transfers/create/store/addKittedProduct";
import { selectTransferCreate } from "transfers/create/store/TransferCreateSelectors";
import { getReplenishmentUnifiedInventory } from "./replenishment/warehouse-selection/actions/getReplenishmentUnifiedInventory";
import { DskuQty, InventoryPool } from "@deliverr/business-types";
import { getProductChooserKittedDSKUDetails } from "common/components/ProductChooser/ProductChooserSelector";
import { removeKittedProduct } from "transfers/create/store/removeKittedProduct";
import { useCallback, useMemo } from "react";
import { DistributionChannel } from "@deliverr/replenishment-client";
import { ReplenishmentWarehouseSelectionState } from "transfers/create/store";
import { getSelectedProductsLotNumbersList } from "./utils/getSelectedProductsLotNumbersList";
import { selectAnyIsLoading } from "common/store/selectors/selectAnyIsLoading";
import { ReplenishmentConstants } from "transfers/constants/ReplenishmentConstants";

const isNextStepDisabled = (
  isInventoryLoading: boolean,
  selectedProducts: Record<string, SelectedProductData>,
  warehouseSelection: ReplenishmentWarehouseSelectionState
): boolean => {
  return (
    isInventoryLoading ||
    isEmpty(selectedProducts) ||
    isNil(warehouseSelection.selectedWarehouseId) ||
    !!find(selectedProducts, (val) => !isEmpty(val.errorMessage)) ||
    some(selectedProducts, (product) => !product.qty)
  );
};

const getAvailableInventory = (
  dsku: string,
  inventoryPool: InventoryPool,
  selectedProducts: Record<string, SelectedProductData>
): number => {
  const product = selectedProducts[dsku];
  return (inventoryPool === InventoryPool.STORAGE ? product?.storageOnHandQty : product?.onHandQty) ?? 0;
};

export const useTransferUnifiedProductSelectionStep = () => {
  const dispatch = useDispatch();
  const {
    distributionChannel,
    isEachPickingOrder,
    warehouseSelection,
    selectedProducts,
    bulkProductUploadResult,
    selectedProductsLotNumbers,
  } = useSelector(selectTransferCreate);

  const isInventoryLoading = useSelector(selectAnyIsLoading)(CreateTransferLoader.FetchStorageInventoryLoader);
  const isNextStepLoading = useSelector(selectAnyIsLoading)(CreateTransferLoader.NextStepLoader);
  const dskuQuantities: DskuQty[] = useMemo(
    () => values(selectedProducts).map((product) => ({ dsku: product.dsku, qty: product.qty })),
    [selectedProducts]
  );

  const isKittingEnabled =
    useFeatureOn(FeatureName.PrepKittingRSToDTCEnabled) &&
    distributionChannel === DistributionChannel.ECOM_VIA_CROSSDOCK;

  const isEachPickingOn = useFeatureOn(FeatureName.WholesaleOrderEachPicking);

  const isDefaultPoolB2BMerchant = useFeatureOn(FeatureName.DefaultPoolB2BMerchant);

  const inventoryPool = isDefaultPoolB2BMerchant ? InventoryPool.DEFAULT : InventoryPool.STORAGE;

  const shouldShowEachPickButton =
    includes(ReplenishmentConstants.SUPPORTED_EACH_PICK_CHANNELS, distributionChannel) && isEachPickingOn;

  const shouldShowLotNumberDropdown = useFeatureOn(FeatureName.ReplenishmentsLotBasedOutbounds) && !isKittingEnabled;

  const shouldUseCasePack = !isEachPickingOrder || isKittingEnabled;

  const addProduct = useCallback(
    (dsku: string, product: SelectedProductData) => {
      const dskuList = [...dskuQuantities, { dsku }];
      const dskus = dskuList.map((item) => item.dsku);

      if (isKittingEnabled && product.isKit && product.caseQty === 1) {
        dispatch(addKittedProduct(product));
      } else {
        const productWithoutInventory = {
          ...product,
          storageOnHandQty: undefined,
          storageUnfilledOrderQty: undefined,
          storageUnavailableQty: undefined,
          onHandQty: 0,
          unfilledOrderQty: 0,
          unavailableQty: 0,
        };
        dispatch(transferCreateAddProduct(dsku, productWithoutInventory));
      }

      dispatch(getReplenishmentUnifiedInventory(dskus));
    },
    [dispatch, dskuQuantities, isKittingEnabled]
  );

  const kitDskuDetails = useSelector(getProductChooserKittedDSKUDetails);

  const removeProduct = useCallback(
    (dsku: string) => {
      if (isKittingEnabled && kitDskuDetails?.[dsku]) {
        dispatch(removeKittedProduct(dsku, dskuQuantities));
      } else {
        dispatch(transferCreateRemoveProduct(dsku));
      }

      const remainingDskus = dskuQuantities.filter((item) => item.dsku !== dsku).map((item) => item.dsku);
      if (!isEmpty(remainingDskus)) {
        dispatch(getReplenishmentUnifiedInventory(remainingDskus));
      }
    },
    [dispatch, dskuQuantities, isKittingEnabled, kitDskuDetails]
  );

  const updateQty = useCallback(
    (dsku: string, totalQuantity: number) => {
      const availableInventory = getAvailableInventory(dsku, inventoryPool, selectedProducts);

      const validQuantity = clamp(isInteger(totalQuantity) ? totalQuantity : 0, 0, availableInventory);
      dispatch(transferCreateUpdateProductQty(dsku, validQuantity));
    },
    [dispatch, selectedProducts, inventoryPool]
  );

  const updateNumberOfCases = useCallback(
    (dsku: string, numberOfCases: number) => {
      const product = selectedProducts[dsku];
      const availableInventory = getAvailableInventory(dsku, inventoryPool, selectedProducts);
      const caseQty = product?.caseQty ?? 1;

      const maxNumberOfCases = Math.floor(availableInventory / caseQty);
      const totalCases = clamp(isInteger(numberOfCases) ? numberOfCases : 0, 0, maxNumberOfCases);

      dispatch(transferCreateUpdateProductNumberOfCases(dsku, totalCases));
    },
    [dispatch, selectedProducts, inventoryPool]
  );

  const handleEachPickingToggle = useCallback(() => {
    dispatch(transferCreateSetIsEachPickingOrderFlag(!isEachPickingOrder));
  }, [dispatch, isEachPickingOrder]);

  const selectedProductsLotNumbersList = useMemo(
    () => getSelectedProductsLotNumbersList(map(dskuQuantities, "dsku"), warehouseSelection),
    [dskuQuantities, warehouseSelection]
  );

  const updateLotNumber = useCallback(
    (dsku: string, lotNumber?: string) => {
      const lotDetails = isEmpty(lotNumber)
        ? undefined
        : selectedProductsLotNumbersList?.[dsku]?.find((lot) => lot.lotNumber === lotNumber);

      dispatch(transferCreateUpdateProductLotNumber(dsku, lotDetails));
      updateNumberOfCases(dsku, 0);
    },
    [dispatch, selectedProductsLotNumbersList, updateNumberOfCases]
  );
  const shouldShowBulkProductUploadReport = !isEmpty(selectedProducts) && !isEmpty(bulkProductUploadResult);

  return {
    addProduct,
    removeProduct,
    updateQty,
    updateNumberOfCases,
    updateLotNumber,
    handleEachPickingToggle,
    isNextDisabled: isNextStepDisabled(isInventoryLoading, selectedProducts, warehouseSelection),
    isNextStepLoading,
    isEachPickingOrder,
    selectedProducts: values(selectedProducts),
    selectedProductsLotNumbersList,
    selectedProductsLotNumbers,
    distributionChannel,
    inventoryPool,
    isKittingEnabled,
    shouldShowEachPickButton,
    shouldShowLotNumberDropdown,
    shouldUseCasePack,
    shouldShowBulkProductUploadReport,
  };
};
